// HTTP Client - Utility functions para fetch con retry y auth

import { buildGatewayNetworkError } from './api-error';

type FetchOptions = RequestInit & {
  timeoutMs?: number;
  retry?: number;
  context?: string;
  envVar?: string;
  baseUrl?: string;
};

async function fetchWithAuth(url: string, options: FetchOptions = {}): Promise<Response> {
  // Default timeouts: GET=45s (increased due to slow Azure PostgreSQL queries), Write operations=60s
  // NOTE: Users endpoint can take 30+ seconds for large datasets
  const method = options.method || 'GET';
  const defaultTimeout = method === 'GET' ? 45000 : 60000;
  const { timeoutMs = defaultTimeout, retry: retryOpt, context, envVar, baseUrl, ...requestInit } = options;
  const token = localStorage.getItem('accessToken');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(requestInit.headers as Record<string, string>)
  };

  if (token) headers['Authorization'] = `Bearer ${token}`;

  const retry = retryOpt ?? (requestInit.method === 'GET' || !requestInit.method ? 2 : 0);

  // Rewrite relative /api calls to absolute gateway base when VITE_API_URL is provided
  let finalUrl = url;
  const API_BASE = (import.meta as any).env?.VITE_API_URL?.trim?.();
  if (typeof API_BASE === 'string' && API_BASE && url.startsWith('/api')) {
    const base = API_BASE.replace(/\/$/, '');
    const rest = url.replace(/^\/api\/v1/, '');
    finalUrl = base + rest;
  }

  for (let attempt = 0; attempt <= retry; attempt++) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort('timeout'), timeoutMs);
    try {
      const response = await fetch(finalUrl, { ...requestInit, headers, signal: ctrl.signal });
      clearTimeout(t);

      // PROJ-035: Enhanced 401 handling with expiration type detection
      if (response.status === 401) {
        // Try to parse error response to detect expiration type
        let eventDispatched = false;
        try {
          const errorData = await response.clone().json();
          const errorCode = errorData?.error?.code;

          // Detect expiration type based on error code
          if (errorCode === 'SESSION_IDLE_EXPIRED') {
            window.dispatchEvent(new CustomEvent('session:idle-expired'));
            eventDispatched = true;
          } else if (errorCode === 'SESSION_ABSOLUTE_EXPIRED') {
            window.dispatchEvent(new CustomEvent('session:absolute-expired'));
            eventDispatched = true;
          } else if (errorCode === 'SESSION_EXPIRED') {
            // Token expired - dispatch generic expired event
            window.dispatchEvent(new CustomEvent('session:expired'));
            eventDispatched = true;
          } else if (errorCode === 'INVALID_TOKEN') {
            // Invalid token - also treat as expired
            window.dispatchEvent(new CustomEvent('session:expired'));
            eventDispatched = true;
          } else if (errorCode === 'MISSING_TOKEN') {
            // No token provided - require login
            window.dispatchEvent(new CustomEvent('session:expired'));
            eventDispatched = true;
          }
        } catch (e) {
          // Ignore JSON parse errors
        }

        // If no specific event was dispatched, dispatch generic session:expired
        // This ensures the re-auth modal shows for any 401 response
        if (!eventDispatched) {
          window.dispatchEvent(new CustomEvent('session:expired'));
        }

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('currentUser');
      }

      // Respect Retry-After for 429/503 and retry transient 50x
      if ([429, 502, 503, 504].includes(response.status) && attempt < retry) {
        const retryAfter = response.headers.get('Retry-After');
        let delayMs = 300 * (attempt + 1);
        if (retryAfter) {
          const s = Number(retryAfter);
          if (!Number.isNaN(s) && s > 0) delayMs = s * 1000;
        }
        // jitter (±25%)
        const jitter = Math.floor(delayMs * (Math.random() * 0.5 - 0.25));
        await new Promise((r) => setTimeout(r, delayMs + jitter));
        continue;
      }

      return response;
    } catch (err: any) {
      clearTimeout(t);
      const isAbort = err?.name === 'AbortError' || String(err).includes('timeout');
      if (attempt < retry && (isAbort || String(err).includes('TypeError'))) {
        await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
        continue;
      }
      if (context && baseUrl) {
        throw buildGatewayNetworkError({
          context,
          configuredUrl: baseUrl,
          envVar,
          detail: err,
          suggestion: baseUrl.includes('/api')
            ? baseUrl.replace(/3001/g, '3101')
            : undefined,
        });
      }
      throw err;
    }
  }

  // Should not reach here
  return fetch(finalUrl, { ...requestInit, headers });
}

// HTTP Client wrapper with convenient methods
const httpClient = {
  async get<T = any>(url: string, options?: Omit<FetchOptions, 'method' | 'body'>): Promise<{ data: T }> {
    const response = await fetchWithAuth(url, { ...options, method: 'GET' });
    const data = await response.json();
    return { data };
  },

  async post<T = any>(url: string, data?: any, options?: Omit<FetchOptions, 'method'>): Promise<{ data: T }> {
    const response = await fetchWithAuth(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
    const responseData = await response.json();
    return { data: responseData };
  },

  async put<T = any>(url: string, data?: any, options?: Omit<FetchOptions, 'method'>): Promise<{ data: T }> {
    const response = await fetchWithAuth(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
    const responseData = await response.json();
    return { data: responseData };
  },

  async delete<T = any>(url: string, options?: Omit<FetchOptions, 'method' | 'body'>): Promise<{ data: T }> {
    const response = await fetchWithAuth(url, { ...options, method: 'DELETE' });
    const responseData = await response.json();
    return { data: responseData };
  }
};

/**
 * Fetch wrapper for PUBLIC endpoints (no authentication required)
 * Used for invitation validation/completion before user is authenticated
 */
async function fetchPublic(url: string, options: Omit<FetchOptions, 'retry'> = {}): Promise<Response> {
  const { timeoutMs = 30000, context, envVar, baseUrl, ...requestInit } = options;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(requestInit.headers as Record<string, string>)
  };

  // Rewrite relative /api calls to absolute gateway base when VITE_API_URL is provided
  let finalUrl = url;
  const API_BASE = (import.meta as any).env?.VITE_API_URL?.trim?.();
  if (typeof API_BASE === 'string' && API_BASE && url.startsWith('/api')) {
    const base = API_BASE.replace(/\/$/, '');
    const rest = url.replace(/^\/api\/v1/, '');
    finalUrl = base + rest;
  }

  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort('timeout'), timeoutMs);

  try {
    const response = await fetch(finalUrl, { ...requestInit, headers, signal: ctrl.signal });
    clearTimeout(t);
    return response;
  } catch (err: any) {
    clearTimeout(t);
    if (context && baseUrl) {
      throw buildGatewayNetworkError({
        context,
        configuredUrl: baseUrl,
        envVar,
        detail: err,
      });
    }
    throw err;
  }
}

export {
  fetchWithAuth,
  fetchPublic,
  httpClient,
  httpClient as default
};
